import { coreActions } from '@sonar-software/sonar-core';
import { DeconstructedURL } from '@sonar-software/sonar-ts-lib';
import { ErrorOrFailStatusCode } from '@sonar-software/sonar-http';
import { GatewayContent } from './general';
import { useNavigate } from 'react-router-dom';

import { FULL_ROUTES, QUERY } from '@sonar-software/sonar-ts-constants';
import React, { useEffect, useRef, useState } from 'react';
import { useAction, useAuthToken, useOnboardingProgress, useOnMount, useSalesforceLink } from '@hooks';

export const ConfirmLinkRouter: React.FC = () => {
	const navigate = useNavigate();
	const link = useSalesforceLink();
	const { isLoggedIn } = useAuthToken();
	const { progress, updateProgress } = useOnboardingProgress();

	const [ code, setCode ] = useState('');
	const isCodeUsedRef = useRef<boolean>(false);
	const isOnboardingRef = useRef<boolean>(false);

	const refreshOrgs = useAction(coreActions.requestGetCoreOrganizationsAll);

	useOnMount(() => {
		const currentUrl = new DeconstructedURL(window.location.href);
		const { subdomain: currentSubdomain, searchParams } = currentUrl;
		const [ targetSubdomain, onboarding ] = (searchParams.get(QUERY.STATE) ?? '').split(';');

		if (targetSubdomain && targetSubdomain !== currentSubdomain) {
			currentUrl.subdomain = targetSubdomain;
			window.location.replace(currentUrl.toString());

			return;
		}

		const salesforceCode = searchParams.get(QUERY.CODE) ?? '';

		if (!isLoggedIn || !salesforceCode) {
			window.location.replace('/');
		}

		isOnboardingRef.current = Boolean(onboarding);

		setCode(salesforceCode);
	});

	useEffect(() => {
		if (!code || isCodeUsedRef.current) {
			return;
		}

		const isOnboarding = isOnboardingRef.current;

		if (isOnboarding && !progress) {
			return;
		}

		isCodeUsedRef.current = true;

		link({
			code,
			onResponse: (result, statusCode) => {
				const target = isOnboarding ? FULL_ROUTES.ONBOARDING_CONFIRM : FULL_ROUTES.ORGANIZATION_CONFIRM;

				if (result) {
					refreshOrgs();

					if (isOnboarding) {
						updateProgress({
							isSalesforceConnected: true,
							isSalesforceSynced: false,
							organizationId: result.id
						});
					} else {
						updateProgress({
							isSalesforceConnected: true,
							isSalesforceSynced: false
						});
					}

					navigate(target, { replace: true });
				} else if (statusCode === ErrorOrFailStatusCode.Forbidden) {
					updateProgress({
						isSalesforceConnected: true,
						isSalesforceSynced: false,
						missingSalesforceSyncPermission: true
					});

					navigate(target, { replace: true });
				} else {
					window.location.replace('/');
				}
			},
			organizationId: isOnboarding ? progress?.organizationId : undefined
		});
	}, [ code, link, navigate, progress, refreshOrgs, updateProgress ]);

	return <GatewayContent />;
};
